import React, { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Plus } from "@phosphor-icons/react";
import { createStory } from "@/lib/api";
import { toast } from "sonner";

export default function NewStoryDialog({ onCreated }) {
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState("");
  const [ringTag, setRingTag] = useState("3");
  const [viewerMode, setViewerMode] = useState("POV_ONLY");
  const [rawText, setRawText] = useState("");
  const [saving, setSaving] = useState(false);

  const handleSubmit = async () => {
    if (!title.trim() || !rawText.trim()) {
      toast.error("Title and story text are required");
      return;
    }
    setSaving(true);
    try {
      const story = await createStory({
        title: title.trim(),
        ring_tag: parseInt(ringTag, 10),
        viewer_mode: viewerMode,
        raw_text: rawText,
      });
      toast.success("Story intake created");
      setOpen(false);
      setTitle("");
      setRawText("");
      setRingTag("3");
      setViewerMode("POV_ONLY");
      onCreated(story);
    } catch (e) {
      toast.error("Failed to create story");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid="new-story-btn" className="bg-blue-600 hover:bg-blue-500 text-white rounded-sm">
          <Plus size={16} className="mr-2" />
          New Story Intake
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-slate-900 border-slate-800 max-w-2xl">
        <DialogHeader>
          <DialogTitle className="font-heading uppercase tracking-wider">Stage 1 — Story Intake</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label className="text-xs text-slate-400 font-mono uppercase tracking-wider">Title</Label>
            <Input
              data-testid="new-story-title-input"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Every Ruin Needs a Replay"
              className="bg-slate-950 border-slate-800 rounded-sm"
            />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs text-slate-400 font-mono uppercase tracking-wider">Ring Tag</Label>
            <Select value={ringTag} onValueChange={setRingTag}>
              <SelectTrigger data-testid="new-story-ring-select" className="bg-slate-950 border-slate-800 rounded-sm">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-slate-900 border-slate-800">
                <SelectItem value="1">Ring 1 · Terminal-bound</SelectItem>
                <SelectItem value="2">Ring 2 · Skit/Matchup</SelectItem>
                <SelectItem value="3">Ring 3 · Narrative (default)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs text-slate-400 font-mono uppercase tracking-wider">Viewer Embodiment</Label>
            <Select value={viewerMode} onValueChange={setViewerMode}>
              <SelectTrigger data-testid="new-story-viewer-mode-select" className="bg-slate-950 border-slate-800 rounded-sm">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-slate-900 border-slate-800">
                <SelectItem value="POV_ONLY">POV_ONLY — viewer is the camera, never visible (default)</SelectItem>
                <SelectItem value="HANDS_ALLOWED">HANDS_ALLOWED — viewer's hands/shadow may appear</SelectItem>
                <SelectItem value="VISIBLE_PROTAGONIST">VISIBLE_PROTAGONIST — viewer is an on-screen character</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-[11px] text-slate-600">Controls how the manifest generator treats the viewer/protagonist camera across every shot.</p>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs text-slate-400 font-mono uppercase tracking-wider">
              Raw Story Text (Acts, SFX cues, visual cues, dialogue)
            </Label>
            <Textarea
              data-testid="new-story-text-input"
              value={rawText}
              onChange={(e) => setRawText(e.target.value)}
              placeholder="Act I: ..."
              rows={10}
              className="bg-slate-950 border-slate-800 rounded-sm font-mono text-sm"
            />
          </div>
        </div>
        <DialogFooter>
          <Button
            data-testid="new-story-submit-btn"
            onClick={handleSubmit}
            disabled={saving}
            className="bg-blue-600 hover:bg-blue-500 text-white rounded-sm w-full"
          >
            {saving ? "Creating…" : "Create Story"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
